import { html, nothing } from "lit";
import type { ControlUiBuildInfo } from "../controllers/developer.ts";

export type DeveloperProps = {
  running: boolean;
  infoLoading: boolean;
  info: ControlUiBuildInfo | null;
  infoError: string | null;
  lines: string[];
  error: string | null;
  lastOk: boolean | null;
  chatModelLabel: string | null;
  chatSessionKey: string | null;
  onRefreshInfo: () => void;
  onBuild: () => void;
  onBuildAndReload: () => void;
  onAiBuildAndReload: () => void;
  onClear: () => void;
  onReload: () => void;
  onCleanRestart: () => void;
};

async function copyCommand(text: string) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    // ignore clipboard failures
  }
}

function renderCommandRow(label: string, command: string, note?: string) {
  return html`
    <div class="dev-command-row">
      <div class="row" style="justify-content: space-between; gap: 8px;">
        <div class="muted">${label}</div>
        <button class="btn btn--sm" type="button" @click=${() => void copyCommand(command)}>
          Copy
        </button>
      </div>
      ${note ? html`<div class="card-sub" style="margin-top: 4px;">${note}</div>` : nothing}
      <pre class="code-block dev-command" tabindex="0" @click=${() => void copyCommand(command)}>${command}</pre>
    </div>
  `;
}

function renderPaths(info: ControlUiBuildInfo) {
  const rows: Array<[string, string | null]> = [
    ["Repo root", info.repoRoot],
    ["package.json", info.packageJsonPath],
    ["UI build script", info.uiScriptPath],
    ["Built index.html", info.indexPath],
    ["Node", info.nodePath],
    ["pnpm", info.pnpmPath],
  ];
  return html`
    <dl class="dev-path-list">
      ${rows.map(
        ([label, value]) => html`
          <dt class="muted">${label}</dt>
          <dd class="mono">${value ?? "—"}</dd>
        `,
      )}
    </dl>
  `;
}

function renderGatewayCommands(props: DeveloperProps) {
  const info = props.info;
  if (props.infoLoading && !info) {
    return html`
      <div class="muted" style="margin-top: 12px">Loading gateway paths…</div>
    `;
  }
  if (props.infoError) {
    return html`<div class="callout danger" style="margin-top: 12px;">${props.infoError}</div>`;
  }
  if (!info) {
    return nothing;
  }
  if (!info.available || !info.shell) {
    return html`
      <div class="callout warn" style="margin-top: 12px;">
        ${
          info.message ??
          "Gateway cannot resolve a Jaisiu checkout on this host. Run the commands below from your own checkout, or deploy with the repo mounted."
        }
      </div>
      ${renderPaths(info)}
    `;
  }

  return html`
    <div class="stack" style="margin-top: 16px; gap: 16px;">
      ${renderPaths(info)}
      ${renderCommandRow(
        "pnpm ui:build (recommended)",
        info.shell.pnpmUiBuild,
        "Same as package.json script on the gateway host.",
      )}
      ${renderCommandRow(
        "Automated build (Developer button)",
        info.shell.nodeUiBuild,
        "Runs node scripts/ui.js build — equivalent to pnpm ui:build.",
      )}
      ${renderCommandRow("cd only", info.shell.cdRepo)}
      ${renderCommandRow(
        "Clean restart (stop + tsdown + ui:build + start)",
        info.shell.cleanRestart,
        "Same as the Clean restart button below. Log defaults to /tmp/jaisiu-clean-restart.log",
      )}
    </div>
  `;
}

export function renderDeveloper(props: DeveloperProps) {
  const terminalText =
    props.lines.length > 0 ? props.lines.join("\n") : "Build output will appear here.";
  const canReload = props.lastOk === true && !props.running;
  const canBuild = props.info?.available === true && !props.running;
  const canCleanRestart = props.info?.available === true && !props.running;
  const canAiBuild = canBuild && Boolean(props.chatSessionKey?.trim());
  const modelHint = props.chatSessionKey?.trim()
    ? `AI rebuild uses model “${props.chatModelLabel ?? "session default"}” on session ${props.chatSessionKey}.`
    : "Open a chat tab and pick a model in the composer toolbar first.";

  return html`
    <div class="stack developer-page" style="gap: 16px;">
      <section class="card developer-panel">
        <div class="row" style="justify-content: space-between; align-items: flex-start;">
          <div>
            <div class="card-title">Run on gateway host</div>
            <div class="card-sub">
              Copy a full-path shell command for SSH or a local terminal on the machine running the
              gateway. The button below runs the same build via the gateway API.
            </div>
          </div>
          <button
            class="btn"
            ?disabled=${props.infoLoading}
            @click=${props.onRefreshInfo}
          >
            ${props.infoLoading ? "Refreshing…" : "Refresh paths"}
          </button>
        </div>
        ${renderGatewayCommands(props)}
      </section>

      <section class="card developer-panel">
        <div class="row" style="justify-content: space-between; align-items: flex-start;">
          <div>
            <div class="card-title">Build from browser</div>
            <div class="card-sub">
              Streams <span class="mono">pnpm ui:build</span> output live. Reload this page after
              success to load <span class="mono">dist/control-ui/</span>.
              <strong>Build &amp; reload</strong> refreshes automatically. <strong>AI rebuild</strong>
              asks the chat agent (selected model) to fix build errors, then builds and reloads.
            </div>
          </div>
          <div class="row" style="gap: 8px; flex-wrap: wrap;">
            <button
              class="btn primary"
              ?disabled=${!canBuild}
              @click=${props.onBuild}
            >
              ${props.running ? "Building…" : "Run pnpm ui:build"}
            </button>
            <button class="btn" ?disabled=${!canBuild} @click=${props.onBuildAndReload}>
              ${props.running ? "Working…" : "Build & reload"}
            </button>
            <button
              class="btn"
              ?disabled=${!canAiBuild}
              title=${canAiBuild ? modelHint : "Select a chat session and model first"}
              @click=${props.onAiBuildAndReload}
            >
              ${props.running ? "Working…" : "AI rebuild & reload"}
            </button>
            <button class="btn" ?disabled=${props.running} @click=${props.onClear}>Clear</button>
            <button class="btn" ?disabled=${!canReload} @click=${props.onReload}>Reload page</button>
            <button
              class="btn btn--danger"
              ?disabled=${!canCleanRestart}
              title="Stop gateway, rebuild dist + Control UI, start gateway again"
              @click=${props.onCleanRestart}
            >
              ${props.running ? "Working…" : "Clean restart"}
            </button>
          </div>
        </div>

        <div class="card-sub muted" style="margin-top: 8px;">${modelHint}</div>

        ${
          !canBuild && !props.running && props.info && !props.info.available
            ? html`
                <div class="callout warn" style="margin-top: 12px">
                  Browser build is disabled until the gateway can see a Jaisiu checkout. Use the shell commands
                  above on the gateway host.
                </div>
              `
            : nothing
        }
        ${
          props.error
            ? html`<div class="callout danger" style="margin-top: 12px;">${props.error}</div>`
            : props.lastOk === true
              ? html`
                  <div class="callout success" style="margin-top: 12px">
                    Build finished. Reload the page to use the new assets.
                  </div>
                `
              : nothing
        }

        <pre class="code-block dev-terminal" aria-label="Build output">${terminalText}</pre>
      </section>
    </div>
  `;
}
